/**
 * PDP guide drawers (Colour, Size, Storage): each time a drawer opens,
 * mark the option the customer has selected and open its row. The Colour
 * Guide (data-guide-mode="hero") also swaps its family's swatch to the
 * selected colour and outlines that colour. Read on open rather than on
 * render because fabric is picked without a reload.
 */
(() => {
  /**
   * @param {HTMLElement} guide
   * @returns {HTMLInputElement[]} The guide option's radios on the page
   */
  function getOptionRadios(guide) {
    const name = guide.dataset.guideOption || '';
    // The whole product section: sticky-product-info.js moves option blocks
    // out of #product-variants after load
    const root = document.getElementById(`shopify-section-${guide.dataset.sectionId}`)
      || document;
    return [...root.querySelectorAll('input[type="radio"]')].filter(
      (input) => input.name === name,
    );
  }

  /**
   * @param {HTMLElement} guide
   * @returns {string}
   */
  function getSelectedValue(guide) {
    const checked = getOptionRadios(guide).find((input) => input.checked);
    return checked ? checked.value : '';
  }

  /**
   * Hides guide rows whose option is hidden on the page because the
   * selected size isn't sold with it (data-guide-hide-unavailable).
   * @param {HTMLElement} guide
   */
  function hideUnavailable(guide) {
    const unavailable = new Set(
      getOptionRadios(guide)
        .filter((input) => input.nextElementSibling?.classList.contains('js-unavailable-variant'))
        .map((input) => input.value),
    );
    guide.querySelectorAll('[data-guide-value]').forEach((item) => {
      item.hidden = unavailable.has(item.dataset.guideValue);
    });
  }

  /**
   * Shows the selected colour as its family's swatch; other families
   * go back to their own first colour.
   * @param {HTMLElement} guide
   * @param {string} value
   */
  function swapFamilySwatch(guide, value) {
    guide.querySelectorAll('[data-guide-family]').forEach((family) => {
      const hero = family.querySelector('summary .pdp-colour-guide__swatch');
      if (!hero) return;
      const colour = [...family.querySelectorAll('[data-guide-value]')].find(
        (item) => item.dataset.guideValue === value,
      );
      const swatch = colour?.querySelector('.pdp-colour-guide__colour-swatch');
      hero.style.backgroundImage = swatch
        ? getComputedStyle(swatch).backgroundImage
        : '';
      hero.style.backgroundColor = swatch
        ? getComputedStyle(swatch).backgroundColor
        : '';
    });
  }

  /**
   * @param {HTMLElement} drawer
   */
  function highlight(drawer) {
    drawer.querySelectorAll('[data-guide-option]').forEach((guide) => {
      const value = getSelectedValue(guide);

      if (guide.dataset.guideMode === 'hero') {
        swapFamilySwatch(guide, value);
      }

      if (guide.hasAttribute('data-guide-hide-unavailable')) {
        hideUnavailable(guide);
      }

      guide.querySelectorAll('[data-guide-value]').forEach((item) => {
        const isSelected = Boolean(value) && item.dataset.guideValue === value;
        item.classList.toggle('is-selected', isSelected);
        if (isSelected) {
          item.setAttribute('aria-current', 'true');
        } else {
          item.removeAttribute('aria-current');
        }
      });

      guide.querySelectorAll('[data-guide-family]').forEach((family) => {
        const hasSelected = family.matches('[data-guide-value].is-selected')
          || Boolean(family.querySelector('.is-selected'));
        family.classList.toggle('is-selected', hasSelected);
        family.open = hasSelected;
      });

      guide.querySelector('.is-selected')?.scrollIntoView({ block: 'nearest' });
    });
  }

  document.addEventListener(
    'toggle',
    (event) => {
      const drawer = event.target;
      if (event.newState !== 'open') return;
      if (!(drawer instanceof HTMLElement) || !drawer.matches('.pdp-info-drawer')) return;
      highlight(drawer);
    },
    true,
  );
})();
